import { format, isSameMonth, startOfMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import React from 'react';
import { Button } from '@components/ui/button';
import { Calendar } from '@components/ui/calendar';
import { Input } from '@components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@components/ui/popover';
import { cn } from '@core/utils/cn';
import { FieldError, FieldLabel } from '../textfield';

export interface DatePickerProps {
    label?: string;
    value: Date | null;
    onChange(value: Date | null): void;
    error?: string;
    disabled?: boolean;
    required?: boolean;
    clearable?: boolean;
    minDate?: Date;
    maxDate?: Date;
    className?: string;
}

const isOutOfRange = (date: Date, min?: Date, max?: Date) =>
    (min ? date < min : false) || (max ? date > max : false);

const defaultMonth = ({ value, minDate, maxDate }: DatePickerProps) => {
    if (value) {
        return value;
    }
    const today = new Date();
    if (maxDate && today > maxDate) {
        return maxDate;
    }
    if (minDate && today < minDate) {
        return minDate;
    }
    return undefined;
};

const DateButton = ({
    value,
    disabled,
    error,
    pattern,
    onClear
}: {
    value: Date | null;
    disabled?: boolean;
    error?: string;
    pattern: string;
    onClear?(): void;
}) => (
    <div className="relative">
        <PopoverTrigger asChild>
            <Button
                type="button"
                variant="outline"
                disabled={disabled}
                aria-invalid={Boolean(error)}
                className={cn(
                    'w-full justify-start font-normal',
                    !value && 'text-muted-foreground',
                    onClear && value && 'pr-9'
                )}
            >
                <CalendarIcon />
                {value ? format(value, pattern, { locale: de }) : 'Pick a date'}
            </Button>
        </PopoverTrigger>
        {onClear && value && !disabled && (
            <button
                type="button"
                aria-label="Clear date"
                onClick={onClear}
                className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
            >
                <X className="size-4" />
            </button>
        )}
    </div>
);

const Field = ({
    label,
    required,
    error,
    className,
    children
}: Pick<DatePickerProps, 'label' | 'required' | 'error' | 'className'> & {
    children: React.ReactNode;
}) => (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
        <FieldLabel label={label} required={required} />
        {children}
        <FieldError error={error} />
    </div>
);

export const DatePicker = (props: DatePickerProps) => (
    <Field {...props}>
        <Popover>
            <DateButton
                value={props.value}
                disabled={props.disabled}
                error={props.error}
                pattern="dd.MM.yyyy"
                onClear={
                    props.clearable ? () => props.onChange(null) : undefined
                }
            />
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    locale={de}
                    selected={props.value ?? undefined}
                    onSelect={(date) => props.onChange(date ?? null)}
                    disabled={(date) =>
                        isOutOfRange(date, props.minDate, props.maxDate)
                    }
                    defaultMonth={defaultMonth(props)}
                />
            </PopoverContent>
        </Popover>
    </Field>
);

export const DateTimePicker = (props: DatePickerProps) => {
    const time = props.value ? format(props.value, 'HH:mm') : '';

    const setTime = (text: string) => {
        const [hours, minutes] = text.split(':').map(Number);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return;
        }
        const next = new Date(props.value ?? new Date());
        next.setHours(hours, minutes, 0, 0);
        props.onChange(next);
    };

    const setDate = (date?: Date) => {
        if (!date) {
            return props.onChange(null);
        }
        const next = new Date(date);
        if (props.value) {
            next.setHours(
                props.value.getHours(),
                props.value.getMinutes(),
                0,
                0
            );
        }
        props.onChange(next);
    };

    return (
        <Field {...props}>
            <Popover>
                <DateButton
                    value={props.value}
                    disabled={props.disabled}
                    error={props.error}
                    pattern="dd.MM.yyyy HH:mm"
                    onClear={
                        props.clearable ? () => props.onChange(null) : undefined
                    }
                />
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        locale={de}
                        selected={props.value ?? undefined}
                        onSelect={setDate}
                        disabled={(date) =>
                            isOutOfRange(date, props.minDate, props.maxDate)
                        }
                        defaultMonth={defaultMonth(props)}
                    />
                    <div className="border-t p-3">
                        <Input
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                        />
                    </div>
                </PopoverContent>
            </Popover>
        </Field>
    );
};

const monthNames = Array.from({ length: 12 }, (_, month) =>
    format(new Date(2000, month, 1), 'MMM', { locale: de })
);

export const MonthPicker = (props: DatePickerProps) => {
    const [open, setOpen] = React.useState(false);
    const [year, setYear] = React.useState(new Date().getFullYear());
    const minMonth = props.minDate && startOfMonth(props.minDate);
    const maxMonth = props.maxDate && startOfMonth(props.maxDate);

    const onOpenChange = (next: boolean) => {
        if (next) {
            setYear((defaultMonth(props) ?? new Date()).getFullYear());
        }
        setOpen(next);
    };

    const select = (month: number) => {
        props.onChange(new Date(year, month, 1));
        setOpen(false);
    };

    return (
        <Field {...props}>
            <Popover open={open} onOpenChange={onOpenChange}>
                <DateButton
                    value={props.value}
                    disabled={props.disabled}
                    error={props.error}
                    pattern="MMMM yyyy"
                    onClear={
                        props.clearable ? () => props.onChange(null) : undefined
                    }
                />
                <PopoverContent className="w-auto p-3" align="start">
                    <div className="mb-2 flex items-center justify-between">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Previous year"
                            disabled={
                                minMonth && year <= minMonth.getFullYear()
                            }
                            onClick={() => setYear(year - 1)}
                        >
                            <ChevronLeft className="size-4" />
                        </Button>
                        <span className="text-sm font-medium">{year}</span>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Next year"
                            disabled={
                                maxMonth && year >= maxMonth.getFullYear()
                            }
                            onClick={() => setYear(year + 1)}
                        >
                            <ChevronRight className="size-4" />
                        </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                        {monthNames.map((name, month) => {
                            const date = new Date(year, month, 1);
                            const selected =
                                props.value && isSameMonth(props.value, date);
                            return (
                                <Button
                                    key={name}
                                    type="button"
                                    variant={selected ? 'default' : 'ghost'}
                                    size="sm"
                                    disabled={isOutOfRange(
                                        date,
                                        minMonth,
                                        maxMonth
                                    )}
                                    onClick={() => select(month)}
                                >
                                    {name}
                                </Button>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
        </Field>
    );
};
